/**
 * Identity seam for the UI (docs/planning/databricks-plan.md §V0.5, §V1.3).
 *
 * Components call useAuth() instead of a provider SDK directly.
 * - logto (default): returns useLogto() as-is — behaviour and function
 *   identities are unchanged.
 * - databricks: the Apps proxy has already authenticated the user with the
 *   workspace SSO and the backend reads the identity from proxy headers, so the
 *   session is always "authenticated" and there are no tokens. getIdToken()
 *   returns a fixed placeholder only because existing flows require a non-empty
 *   value; the backend ignores bearer tokens in this mode.
 */
import { useLogto } from '@logto/react';

import { AUTH_MODE } from '../lib/runtimeFlags';

type LogtoAuth = ReturnType<typeof useLogto>;

export type AuthApi = Pick<
  LogtoAuth,
  'isAuthenticated' | 'isLoading' | 'error' | 'signIn' | 'signOut' | 'getAccessToken' | 'getIdToken'
>;

export const PROXY_SESSION_TOKEN = 'databricks-apps-proxy-session';

// Module-level constant: stable identities for hooks' dependency arrays.
const proxyAuth: AuthApi = {
  isAuthenticated: true,
  isLoading: false,
  error: undefined,
  // The workspace session is the only session: "signing in" again is a reload,
  // and there is no app-level sign-out.
  signIn: async () => {
    window.location.reload();
  },
  signOut: async () => {
    window.location.assign('/');
  },
  getAccessToken: async () => undefined as unknown as string,
  getIdToken: async () => PROXY_SESSION_TOKEN,
};

function useProxyAuth(): AuthApi {
  return proxyAuth;
}

function useLogtoAuth(): AuthApi {
  return useLogto();
}

export const useAuth: () => AuthApi = AUTH_MODE === 'databricks' ? useProxyAuth : useLogtoAuth;
